/**
 * EVT-08 — pick an event's time zone from the venue ZIP, not the state.
 *
 * The state map was wrong in every split-zone state (El Paso TX is Mountain, the Florida
 * panhandle and middle Tennessee are Central, eastern Tennessee is Eastern, the Navajo
 * Nation in AZ observes DST, ...). This resolves:
 *   1. the 5-digit ZIP against a compact generated table (ZIP3 default + per-ZIP exceptions),
 *   2. else the state (fallback; also used when the ZIP is missing / unknown / a territory).
 *
 * Every zone returned is one the TimezoneSelector offers, so the select never goes blank
 * (the old map returned America/Boise and America/Indiana/Indianapolis, which it does not).
 *
 * Regenerate the data with crm-frontend/scripts/generate-zip-timezone-data.mjs.
 */
import {
  ZIP3_DEFAULTS,
  ZIP_EXCEPTIONS,
  ZIP_TZ_OPTIONS,
} from "./zipTimezoneData";

export type ZipTimezone = (typeof ZIP_TZ_OPTIONS)[number];

let zip3Map: Map<string, ZipTimezone> | null = null;
let exceptionMap: Map<string, ZipTimezone> | null = null;

function decode(run: string, keyLen: number): Map<string, ZipTimezone> {
  const out = new Map<string, ZipTimezone>();
  const cell = keyLen + 1;
  for (let i = 0; i + cell <= run.length; i += cell) {
    const tz = ZIP_TZ_OPTIONS[Number(run[i + keyLen])];
    if (tz) {
      out.set(run.slice(i, i + keyLen), tz);
    }
  }
  return out;
}

/** State fallback, canonicalised to selector zones. Split states use the majority zone. */
export const STATE_TO_TIMEZONE: Record<string, ZipTimezone> = {
  CT: "America/New_York",
  DE: "America/New_York",
  FL: "America/New_York",
  GA: "America/New_York",
  IN: "America/New_York",
  KY: "America/New_York",
  ME: "America/New_York",
  MD: "America/New_York",
  MA: "America/New_York",
  MI: "America/New_York",
  NH: "America/New_York",
  NJ: "America/New_York",
  NY: "America/New_York",
  NC: "America/New_York",
  OH: "America/New_York",
  PA: "America/New_York",
  RI: "America/New_York",
  SC: "America/New_York",
  VA: "America/New_York",
  VT: "America/New_York",
  WV: "America/New_York",
  DC: "America/New_York",
  AL: "America/Chicago",
  AR: "America/Chicago",
  IL: "America/Chicago",
  IA: "America/Chicago",
  KS: "America/Chicago",
  LA: "America/Chicago",
  MN: "America/Chicago",
  MS: "America/Chicago",
  MO: "America/Chicago",
  NE: "America/Chicago",
  ND: "America/Chicago",
  OK: "America/Chicago",
  SD: "America/Chicago",
  TN: "America/Chicago",
  TX: "America/Chicago",
  WI: "America/Chicago",
  AZ: "America/Phoenix",
  CO: "America/Denver",
  ID: "America/Denver",
  MT: "America/Denver",
  NM: "America/Denver",
  UT: "America/Denver",
  WY: "America/Denver",
  CA: "America/Los_Angeles",
  NV: "America/Los_Angeles",
  OR: "America/Los_Angeles",
  WA: "America/Los_Angeles",
  AK: "America/Anchorage",
  HI: "Pacific/Honolulu",
};

/** "79901", "79901-1234", " 79901 " -> "79901"; anything else -> null. */
export function normalizeZip5(zip: string | null | undefined): string | null {
  const m = /^\s*(\d{5})(?:-?\d{4})?\s*$/.exec(zip ?? "");
  return m ? m[1] : null;
}

export function timezoneForZip(
  zip: string | null | undefined,
): ZipTimezone | null {
  const z = normalizeZip5(zip);
  if (!z) {
    return null;
  }
  exceptionMap ??= decode(ZIP_EXCEPTIONS, 5);
  zip3Map ??= decode(ZIP3_DEFAULTS, 3);
  return exceptionMap.get(z) ?? zip3Map.get(z.slice(0, 3)) ?? null;
}

export interface ResolvedEventTimezone {
  timezone: ZipTimezone;
  source: "zip" | "state";
  /** the normalized ZIP (source zip) or the state code (source state) */
  from: string;
}

/** ZIP first, else state; null when neither resolves. */
export function resolveEventTimezone(
  zip: string | null | undefined,
  state: string | null | undefined,
): ResolvedEventTimezone | null {
  const z = normalizeZip5(zip);
  const byZip = timezoneForZip(z);
  if (z && byZip) {
    return { timezone: byZip, source: "zip", from: z };
  }
  const st = (state ?? "").trim().toUpperCase();
  const byState = STATE_TO_TIMEZONE[st];
  return byState ? { timezone: byState, source: "state", from: st } : null;
}

/**
 * EVT-08b: the venue ZIP alone (no state fallback) — CSV import uses this so an imported event's
 * zone follows the same postal-code-only rule as the event forms. null when the ZIP is missing or
 * not in the table.
 */
export function resolveEventTimezoneByZip(
  zip: string | null | undefined,
): ResolvedEventTimezone | null {
  return resolveEventTimezone(zip, null);
}

/** Short hint shown under the time-zone select: "(from ZIP 79901)" / "(from state TX)". */
export function describeTimezoneSource(
  r: ResolvedEventTimezone | null,
): string {
  if (!r) {
    return "";
  }
  return r.source === "zip" ? `(from ZIP ${r.from})` : `(from state ${r.from})`;
}

/**
 * EVT-08b (owner 2026-10-30) — the event FORMS fill the time zone from the venue postal code ONLY.
 *
 * Sales asked that the zone be "based on the postal code of the location". So, unlike
 * resolveEventTimezone (still used by CSV import and the MSR display fallback), the create
 * wizard and the event form never fill a zone from the state or from a default:
 *   - a recognised 5-digit ZIP (physical / hybrid venue) -> that ZIP's zone, hinted "(from ZIP 79901)";
 *   - no ZIP, an unrecognised ZIP, or a virtual event -> "" (unset): the operator must choose,
 *     and the save / Next validation ("Choose a time zone.") refuses a blank zone;
 *   - editing an event that already has a saved zone -> the saved zone is kept while the ZIP is
 *     unchanged, and also when the new ZIP does not resolve (never cleared, never state-filled).
 *     Only a changed ZIP that resolves replaces it.
 * The caller skips this entirely once the operator picks a zone by hand.
 */
export type AutoEventTimezoneReason =
  | "zip"
  | "saved"
  | "no_zip"
  | "unknown_zip"
  | "virtual";

export interface AutoEventTimezone {
  /** The zone to show; "" = unset (the operator must choose). */
  timezone: string;
  /** Set only when the zone came from the ZIP. */
  resolved: ResolvedEventTimezone | null;
  reason: AutoEventTimezoneReason;
  /** The postal code as considered ("" for a virtual event). */
  postalCode: string;
}

export interface AutoEventTimezoneInput {
  locationType?: string | null;
  postalCode?: string | null;
  /** Edit only: the event as loaded. */
  saved?: {
    timezone?: string | null;
    locationType?: string | null;
    postalCode?: string | null;
  } | null;
}

/** The venue postal code that counts for the zone: none for a virtual event (it has no venue). */
function venueZipKey(
  locationType: string | null | undefined,
  postalCode: string | null | undefined,
): string {
  if (locationType === "virtual") {
    return "";
  }
  const raw = (postalCode ?? "").trim();
  return normalizeZip5(raw) ?? raw;
}

export function autoEventTimezone(
  input: AutoEventTimezoneInput,
): AutoEventTimezone {
  const isVirtual = input.locationType === "virtual";
  const key = venueZipKey(input.locationType, input.postalCode);
  const savedTz = (input.saved?.timezone ?? "").trim();
  if (
    savedTz &&
    key === venueZipKey(input.saved?.locationType, input.saved?.postalCode)
  ) {
    return {
      timezone: savedTz,
      resolved: null,
      reason: "saved",
      postalCode: key,
    };
  }
  const byZip = timezoneForZip(key);
  if (byZip) {
    return {
      timezone: byZip,
      resolved: { timezone: byZip, source: "zip", from: key },
      reason: "zip",
      postalCode: key,
    };
  }
  if (savedTz) {
    return {
      timezone: savedTz,
      resolved: null,
      reason: "saved",
      postalCode: key,
    };
  }
  return {
    timezone: "",
    resolved: null,
    reason: isVirtual ? "virtual" : key ? "unknown_zip" : "no_zip",
    postalCode: key,
  };
}

/** The line under the time-zone select when the zone was NOT auto-filled ("" = say nothing). */
export function timezoneUnsetNote(auto: AutoEventTimezone | null): string {
  if (!auto) {
    return "";
  }
  switch (auto.reason) {
    case "no_zip":
      return "Enter the location's postal code to set the time zone automatically, or choose one.";
    case "unknown_zip":
      return `We couldn't match postal code ${auto.postalCode} to a time zone. Check the postal code, or choose the time zone.`;
    case "virtual":
      return "Virtual events have no venue postal code, so choose the time zone.";
    default:
      return "";
  }
}
